import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { View } from '../App';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy, limit, Timestamp } from 'firebase/firestore';
import { useUI } from '../lib/state';
import './Sidebar.css';
// Logo will be handled via CSS background-image or placeholder
import { useFirestoreAgents } from '../hooks/use-firestore-agents';

interface SidebarProps {
  currentView: View;
  onNavigate: (view: View) => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, onNavigate, onCollapseChange }) => {
  const { user, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { agents, loading: agentsLoading } = useFirestoreAgents();

  const activeAgent = agents.find(agent => agent.status === 'active');

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleCollapseToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (onCollapseChange) {
      onCollapseChange(newState);
    }
  };

  const navItems = [
    { icon: 'dashboard', label: 'Dashboard', view: 'Dashboard' as View },
    { icon: 'code', label: 'Anantha Research', view: 'Developer' as View },
    { icon: 'terminal', label: 'Developer Dashboard', view: 'Developer Dashboard' as View },
    { icon: 'smart_toy', label: 'My Agents', view: 'My Agents' as View },
    { icon: 'chat', label: 'AI Chat', view: 'AI Chat' as View },
    { icon: 'forum', label: 'Conversation History', view: 'Conversation History' as View },
    { icon: 'phone', label: 'Phone Calls', view: 'Phone Calls' as View },
    { icon: 'settings', label: 'Agent Configuration', view: 'Agent Configuration' as View },
    { icon: 'history', label: 'Call History', view: 'Call History' as View },
    { icon: 'payments', label: 'Payment Gateway', view: 'Payment Gateway' as View },
    { icon: 'analytics', label: 'CRM Dashboard', view: 'CRM Dashboard' as View },
    { icon: 'integration_instructions', label: 'Integrations', view: 'Integrations' as View },
  ];

  const { isSidebarOpen } = useUI();

  return (
    <aside 
      className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isSidebarOpen ? 'open' : ''}`}
    >
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="logo">
          <div className="logo-icon">
            <span className="material-symbols-outlined">smart_toy</span>
          </div>
          {!isCollapsed && (
            <div className="logo-text">
              <h2 className="sidebar-brand">Vernika AI</h2>
              <p>Calling Agent</p>
            </div>
          )}
        </div>
        <button
          className="collapse-btn"
          onClick={handleCollapseToggle}
        >
          <span className="material-symbols-outlined">
            {isCollapsed ? 'chevron_right' : 'chevron_left'}
          </span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <button
            key={item.view}
            className={`nav-item ${currentView === item.view ? 'active' : ''}`}
            onClick={() => {
              onNavigate(item.view);
              if (window.innerWidth <= 1024) {
                // Close sidebar on mobile
                // The parent component handles the actual toggle via onNavigate or we can emit an event
                // But since Sidebar doesn't have toggleSidebar prop, we rely on the parent's onNavigate wrapper
                // or we can dispatch a custom event or use the UI context if available
              }
            }}
            title={isCollapsed ? item.label : undefined}
          >
            <span className="nav-icon material-symbols-outlined">
              {item.icon}
            </span>
            {!isCollapsed && (
              <span className="nav-label">
                {item.label}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* User Section */}
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">
            {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
          </div>
          {!isCollapsed && (
            <div className="user-details">
              <span className="user-name">
                {user?.displayName || 'S Surya G'}
              </span>
              <span className="user-plan">
                Free Plan
              </span>
            </div>
          )}
        </div>
        <button
          className="logout-btn"
          onClick={handleLogout}
          title={isCollapsed ? 'Logout' : undefined}
        >
          <span className="material-symbols-outlined">logout</span>
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;